import { format } from 'date-fns'; // npm install date-fns
// These are plain functions, not components, so they read the active language
// from the i18n module rather than from the React context. Every caller that
// renders their output subscribes to the context itself (see useT), so a
// language switch still re-renders them.
import { t, getLanguage } from './i18n';

// Basic function to extract GDrive ID - needs refinement for robustness
export function extractGoogleDriveId(url) {
  if (!url) return null;
  try {
    const parsedUrl = new URL(url);
    // Try common patterns like /file/d/ID/ or /open?id=ID
    const matchD = parsedUrl.pathname.match(/\/file\/d\/([a-zA-Z0-9_-]+)/);
    if (matchD && matchD[1]) {
      return matchD[1];
    }
    const matchId = parsedUrl.searchParams.get('id');
    if (matchId) {
      return matchId;
    }
    // Add more patterns if needed based on the links you encounter
    console.warn("Could not extract Google Drive ID from URL:", url);
    return null;
  } catch (error) {
    console.error("Error parsing Google Drive URL:", url, error);
    return null;
  }
}

// The backend stores naive UTC timestamps and serializes them without a timezone
// offset (e.g. "2025-01-15T10:30:00"). `new Date()` would read such a string as
// *local* time, shifting every displayed time by the viewer's UTC offset. Append
// "Z" so it is parsed as UTC; date-fns then formats in the viewer's local time.
// (date-fns `format` has no timeZone option — passing one is ignored.)
function parseUtc(dateTimeString) {
  const hasZoneInfo = /[zZ]|[+-]\d{2}:?\d{2}$/.test(dateTimeString);
  return new Date(hasZoneInfo ? dateTimeString : `${dateTimeString}Z`);
}

export function formatDateTime(dateTimeString) {
  if (!dateTimeString) return t('N/A');
  try {
    return format(parseUtc(dateTimeString), 'dd/MM/yyyy HH:mm:ss');
  } catch (error) {
    console.error("Error formatting date:", dateTimeString, error);
    return t('Invalid Date');
  }
}

// Day and month only, for places too tight for a full timestamp (the reenact
// jumper chips, where it distinguishes takes recorded weeks apart).
export function formatShortDate(dateTimeString) {
  if (!dateTimeString) return t('N/A');
  try {
    return format(parseUtc(dateTimeString), 'd/M');
  } catch (error) {
    console.error("Error formatting date:", dateTimeString, error);
    return t('Invalid Date');
  }
}

export function formatSeconds(seconds) {
    if (seconds === null || seconds === undefined || isNaN(seconds)) {
        return t('N/A');
    }
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return t('{minutes}m {seconds}s', { minutes: mins, seconds: secs.toString().padStart(2, '0') });
}

export function formatCurrency(amount, currency = 'THB') {
    if (amount === null || amount === undefined || isNaN(amount)) {
        return t('N/A');
    }
     // Thai renders THB as ฿; en-US spells it "THB".
    return new Intl.NumberFormat(getLanguage() === 'th' ? 'th-TH' : 'en-US', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(amount);
}